import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { orderBy } from 'lodash';
import { useState } from 'react';

import { getCreatorApiUrl } from '@client/components/api-urls';
import auth from '@client/components/auth';
import { formatISODate } from '@client/helpers/date-utils';
import { Async, toOption } from '@client/new/helpers/types';
import { FilterOption } from '@design-system/components/dropDown/dropDownFilter';

import { usePaymentMethod } from './usePaymentMethod';

export type useOtpPayoutsProps = {
  date: Date;
  creatorId: number;
};

export type ProductRevenue = {
  name: string;
  unitsSold: number;
  revenue: number;
};

export type ProductsEarnings = {
  products: ProductRevenue[];
  refundsAndDisputes: RefundsAndDisputes;
  totalRevenue: number;
  totalUnitsSold: number;
};

export type RefundsAndDisputes = {
  unitsSold: number;
  revenue: number;
};

export type OtpEarningsBreakdown = {
  date: Date;
  programsEarnings: ProductsEarnings;
  bundlesEarnings: ProductsEarnings;
  refundsAndDisputes: RefundsAndDisputes;
  totalRevenue: number;
  payoutFee: number;
  totalPayout: number;
  paidAt?: string;
  hasPrograms: boolean;
};

export type OtpPayouts = {
  productTypeFilter: {
    options: Array<FilterOption<string>>;
    label: string;
    selectedValue?: ProductType;
    deleteSelection: () => void;
    onSelect: (value?: string) => void;
    show: boolean;
  };
  productEarnings: ProductsEarnings;
  payoutFee: number;
  payoutFeeLabel: string;
  totalPayout: number;
  totalPayoutLabel: string;
  paidAt?: string;
  hasPrograms: boolean;
};

export const productTypeOptions = [
  toOption('Offers', 'Offers'),
  toOption('Programs', 'Program'),
];
const productTypes = productTypeOptions.map((o) => o.value);
type ProductType = (typeof productTypes)[number];

const fetchOtpPayout = async (
  creatorId: number,
  date: Date,
): Promise<OtpEarningsBreakdown> => {
  const url = new URL(
    getCreatorApiUrl(`/one-time-purchase/payout/${creatorId}/dashboard`),
  );

  const response = await axios.get<OtpEarningsBreakdown>(url.toString(), {
    params: {
      date: formatISODate(date),
    },
    headers: {
      Authorization: auth.getToken(),
    },
  });

  return response.data;
};

const getProductEarnings = (
  data: OtpEarningsBreakdown,
  selectedProductType?: ProductType,
): ProductsEarnings => {
  switch (selectedProductType) {
    case 'Offers':
      return {
        ...data.bundlesEarnings,
        products: orderBy(data.bundlesEarnings.products, 'revenue', 'desc'),
      };
    case 'Programs':
      return {
        ...data.programsEarnings,
        products: orderBy(data.programsEarnings.products, 'revenue', 'desc'),
      };
    default:
      return {
        products: orderBy(
          [...data.bundlesEarnings.products, ...data.programsEarnings.products],
          'revenue',
          'desc',
        ),
        totalRevenue: data.totalRevenue,
        refundsAndDisputes: data.refundsAndDisputes,
        totalUnitsSold:
          data.programsEarnings.totalUnitsSold +
          data.bundlesEarnings.totalUnitsSold,
      };
  }
};

export const useOtpPayouts = ({
  date,
  creatorId,
}: useOtpPayoutsProps): Async<OtpPayouts> => {
  const { isFetched, data, isError } = useQuery(
    ['otp-payouts', creatorId, date],
    () => fetchOtpPayout(creatorId, date),
  );

  const [selectedProductType, setProductFilter] = useState<
    ProductType | undefined
  >(undefined);
  const paymentMethod = usePaymentMethod();

  if (isError) {
    return {
      type: 'fetch-error',
      value: 'could not fetch data',
    };
  }

  if (isFetched && data) {
    const productEarnings = getProductEarnings(data, selectedProductType);
    const hasOffersAndPrograms =
      data.bundlesEarnings.products.length > 0 &&
      data.programsEarnings.products.length > 0;

    return {
      type: 'fetched',
      value: {
        productTypeFilter: {
          options: productTypeOptions,
          label: 'Product Type',
          selectedValue: selectedProductType,
          onSelect: (value) => setProductFilter(value as ProductType),
          deleteSelection: () => setProductFilter(undefined),
          show: hasOffersAndPrograms,
        },
        productEarnings,
        hasPrograms: data.hasPrograms,
        totalPayout: data.totalPayout,
        totalPayoutLabel: 'Total Monthly Payout',
        payoutFee: data.payoutFee,
        payoutFeeLabel: `${paymentMethod.name} Transfer Fee`,
        paidAt: data.paidAt,
      },
    };
  }

  return {
    type: 'fetching',
  };
};
